/**
 * <calftalk-configurator> — hydrates the server-rendered configurator.
 *
 * The section renders the initial state in Liquid (first live series, first
 * phrase, first available size, single pair). This component takes over
 * switching: series -> phrase(s) -> size -> pack, re-rendering the sock pair
 * SVG and keeping the real Shopify product form in sync. Submission itself is
 * handled by Horizon's product-form-component, so cart drawer, errors and
 * analytics behave exactly like a product page.
 *
 * The SVG geometry and the text-fitting table are ports of
 * snippets/calftalk-sock.liquid and snippets/calftalk-sock-text.liquid.
 * Keep them in sync. Never use SVG textLength: it distorts the letterforms.
 */

const SOCK_PATH =
  'M 40,10 L 130,10 C 128,70 122,140 120,190 C 119,222 126,244 133,262 C 137,274 130,284 116,284 L 54,284 C 40,284 33,274 37,262 C 44,244 51,222 50,190 C 48,140 42,70 40,10 Z';
const HEEL_PATH =
  'M 24,300 L 24,268 C 24,254 46,247 85,247 C 124,247 146,254 146,268 L 146,300 Z';

let uid = 0;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Estimate a font size for a line of block caps without distorting glyphs. */
function fitSize(text, maxWidth, maxSize) {
  let width = 0;
  for (const ch of String(text)) {
    if (ch === ' ') width += 0.28;
    else if (/[MW]/.test(ch)) width += 0.62;
    else if (/[I1'.]/.test(ch)) width += 0.26;
    else width += 0.47;
  }
  return Math.min(maxSize, maxWidth / Math.max(width, 0.01));
}

/** Render fitted, centred <text> lines. One size per block so lines align. */
function textLines(x, cy, lines, ink, maxWidth, maxSize) {
  const filtered = (lines || []).filter(Boolean);
  if (!filtered.length) return '';
  const size = Math.min(...filtered.map((line) => fitSize(line, maxWidth, maxSize)));
  const lineHeight = size * 1.02;
  const yStart = cy - ((filtered.length - 1) * lineHeight) / 2 + size * 0.35;
  return filtered
    .map(
      (line, index) =>
        `<text x="${x}" y="${(yStart + index * lineHeight).toFixed(2)}" text-anchor="middle" style="font-family: var(--calftalk-font-display); font-weight: 900;" font-size="${size.toFixed(2)}" letter-spacing="0.6" fill="${ink}">${escapeHtml(line)}</text>`
    )
    .join('');
}

/** One sock, translated by offsetX inside a shared viewBox. */
function sockGroup(offsetX, { body, ink, accent, cuff, rib, lines }) {
  const clipId = `calftalk-js-clip-${++uid}`;
  let svg = `<g transform="translate(${offsetX},0)">`;
  svg += `<defs><clipPath id="${clipId}"><path d="${SOCK_PATH}"/></clipPath></defs>`;
  svg += `<path d="${SOCK_PATH}" fill="${body}"/>`;
  svg += `<g clip-path="url(#${clipId})">`;
  svg += `<path d="${HEEL_PATH}" fill="${accent}"/>`;
  if (cuff) {
    svg += `<rect x="20" y="14" width="130" height="9" fill="${accent}"/>`;
    svg += `<rect x="20" y="28" width="130" height="5" fill="${accent}"/>`;
  }
  if (rib) {
    for (let x = 44; x < 128; x += 6) {
      svg += `<line x1="${x}" y1="10" x2="${x - 1}" y2="240" stroke="${ink}" stroke-opacity=".09" stroke-width="2.4"/>`;
      svg += `<line x1="${x}" y1="10" x2="${x}" y2="46" stroke="${ink}" stroke-opacity=".18" stroke-width="2.4"/>`;
    }
  }
  svg += `<text x="85" y="41" text-anchor="middle" style="font-family: var(--calftalk-font-display); font-weight: 800;" font-size="10" letter-spacing="1.5" fill="${ink}" fill-opacity="0.85">CALFTALK CALFTALK</text>`;
  svg += '</g>';
  svg += `<path d="${SOCK_PATH}" fill="none" stroke="var(--calftalk-ink, #0F0F10)" stroke-width="3" stroke-linejoin="round"/>`;
  svg += `<line x1="41.5" y1="46" x2="128.5" y2="46" stroke="var(--calftalk-ink, #0F0F10)" stroke-opacity=".3" stroke-width="2"/>`;
  svg += textLines(85, 140, lines, ink, 64, 26);
  svg += '</g>';
  return svg;
}

function pairSvg(series, phrase, label) {
  const options = {
    body: series.body,
    ink: series.ink,
    accent: series.accent,
    cuff: series.cuff,
    rib: series.rib,
  };
  return (
    `<svg viewBox="0 0 384 320" width="100%" style="max-width: 440px; height: auto;" role="img" aria-label="${escapeHtml(label)}">` +
    sockGroup(0, { ...options, lines: phrase.left }) +
    sockGroup(214, { ...options, lines: phrase.right }) +
    `<path d="M 128,140 L 256,140" stroke="var(--calftalk-ink, #0F0F10)" stroke-width="2.5" fill="none" stroke-dasharray="1 8" stroke-linecap="round"/>` +
    '</svg>'
  );
}

function phraseLabel(phrase) {
  return `${(phrase.left || []).join(' ')} / ${(phrase.right || []).join(' ')}`;
}

class CalftalkConfigurator extends HTMLElement {
  connectedCallback() {
    const dataElement = this.querySelector('[data-calftalk-data]');
    if (!dataElement) return;

    try {
      this.data = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.warn('[calftalk-configurator] Invalid data payload', error);
      return;
    }

    const firstLive = this.data.series.find((series) => series.status === 'live') || this.data.series[0];
    if (!firstLive) return;

    // After a waitlist submit the page reloads; keep that series selected so
    // the confirmation message is actually visible.
    const successElement = this.querySelector('[data-calftalk-waitlist] .calftalk-waitlist__success');
    const successSeries = successElement
      ? this.data.series.find(
          (series) => series.handle === successElement.closest('[data-calftalk-waitlist]')?.dataset.calftalkWaitlist
        )
      : null;
    const initial = successSeries || firstLive;

    this.state = {
      series: initial,
      selected: initial.phrases.length ? [initial.phrases[0]] : [],
      size: this.#defaultSize(initial.phrases[0]),
      pack: this.data.packs[0],
    };
    if (successSeries) {
      this.#renderPhrases();
      requestAnimationFrame(() => this.scrollIntoView({ block: 'center' }));
    }

    this.addEventListener('click', this.#onClick);
    document.addEventListener('click', this.#onDocumentClick);

    this.#renderSizes();
    this.#sync();
    this.#preloadImages();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
  }

  #defaultSize(phrase) {
    if (!phrase || !phrase.variants) return null;
    const available = phrase.variants.find((variant) => variant.available);
    return (available || phrase.variants[0])?.size ?? null;
  }

  /** Clicks from anywhere on the page (series grid, gift ladder) can preselect a series or pack. */
  #onDocumentClick = (event) => {
    const packLink = event.target.closest('[data-calftalk-pack-link]');
    if (packLink) {
      const pack = this.data.packs.find((item) => item.id === packLink.dataset.calftalkPackLink);
      if (pack) {
        if (this.state.series.status !== 'live') {
          const live = this.data.series.find((series) => series.status === 'live');
          if (live) this.#selectSeries(live);
        }
        this.state.pack = pack;
        this.state.selected = this.state.selected.slice(0, pack.pairs);
        this.#sync();
      }
      return;
    }

    const link = event.target.closest('[data-calftalk-series-link]');
    if (!link) return;
    const handle = link.getAttribute('data-calftalk-series-link');
    const series = this.data.series.find((item) => item.handle === handle);
    if (!series) return;
    this.#selectSeries(series);
  };

  #onClick = (event) => {
    const seriesButton = event.target.closest('[data-calftalk-series]');
    if (seriesButton) {
      const series = this.data.series.find((item) => item.handle === seriesButton.dataset.calftalkSeries);
      if (series) this.#selectSeries(series);
      return;
    }

    const phraseButton = event.target.closest('[data-calftalk-phrase]');
    if (phraseButton) {
      this.#togglePhrase(phraseButton.dataset.calftalkPhrase);
      return;
    }

    const sizeButton = event.target.closest('[data-calftalk-size]');
    if (sizeButton) {
      this.state.size = sizeButton.dataset.calftalkSize;
      this.#sync();
      return;
    }

    const packButton = event.target.closest('[data-calftalk-pack]');
    if (packButton) {
      const pack = this.data.packs.find((item) => item.id === packButton.dataset.calftalkPack);
      if (!pack) return;
      this.state.pack = pack;
      // Trim selections that no longer fit the pack size.
      this.state.selected = this.state.selected.slice(0, pack.pairs);
      this.#sync();
    }
  };

  #selectSeries(series) {
    if (this.state.series.handle === series.handle) return;
    this.state.series = series;
    this.state.selected = series.phrases.length ? [series.phrases[0]] : [];
    this.state.size = this.#defaultSize(series.phrases[0]) || this.state.size;
    this.#renderPhrases();
    this.#renderSizes();
    this.#sync();
  }

  #togglePhrase(key) {
    const phrase = this.state.series.phrases.find((item) => String(item.key) === String(key));
    if (!phrase) return;

    const max = this.state.series.status === 'live' ? this.state.pack.pairs : 1;
    const index = this.state.selected.findIndex((item) => String(item.key) === String(key));

    if (max === 1) {
      this.state.selected = [phrase];
    } else if (index >= 0) {
      this.state.selected.splice(index, 1);
    } else {
      if (this.state.selected.length >= max) this.state.selected.shift();
      this.state.selected.push(phrase);
    }

    if (this.state.series.status === 'live' && this.state.pack.id === 'one') {
      this.state.size = this.#resolveSize(phrase);
    }
    this.#sync();
  }

  /** Keep the chosen size if the phrase has it; otherwise fall back. */
  #resolveSize(phrase) {
    if (!phrase.variants) return this.state.size;
    const match = phrase.variants.find((variant) => variant.size === this.state.size);
    return match ? this.state.size : this.#defaultSize(phrase);
  }

  get #lastSelected() {
    return this.state.selected[this.state.selected.length - 1] || this.state.series.phrases[0];
  }

  /**
   * Swap the preview without ever showing an empty stage: the incoming image
   * is downloaded and decoded off-screen first, and the outgoing one stays
   * visible until the replacement is ready. A token guards rapid clicking.
   */
  async #renderStage(series, phrase) {
    const stage = this.querySelector('[data-calftalk-stage]');
    if (!stage) return;

    if (!phrase.image) {
      stage.innerHTML = pairSvg(series, phrase, `${series.title}: ${phraseLabel(phrase)}`);
      return;
    }

    const current = stage.querySelector('img.calftalk-config__stage-img');
    if (current && current.getAttribute('src') === phrase.image) return;

    const token = ++this.#stageToken;
    const img = document.createElement('img');
    img.className = 'calftalk-config__stage-img';
    img.width = 880;
    img.height = 880;
    img.decoding = 'async';
    img.alt = `${series.title}: ${phraseLabel(phrase)}`;
    img.src = phrase.image;
    try {
      await img.decode();
    } catch (error) {
      // Decode can reject (e.g. detached document); the swap below still shows the image.
    }
    if (token !== this.#stageToken) return;
    stage.replaceChildren(img);
  }

  #stageToken = 0;

  /** Warm the browser cache for every phrase image so switching is instant. */
  #preloadImages() {
    const warm = () => {
      for (const series of this.data.series) {
        for (const phrase of series.phrases) {
          if (phrase.image) new Image().src = phrase.image;
        }
      }
    };
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 });
    else setTimeout(warm, 1500);
  }

  #renderPhrases() {
    const container = this.querySelector('[data-calftalk-phrases]');
    if (!container) return;
    container.innerHTML = this.state.series.phrases
      .map(
        (phrase) => `
          <button type="button" class="calftalk-config__phrase" data-calftalk-phrase="${escapeHtml(phrase.key)}" aria-pressed="false">
            <span class="calftalk-config__phrase-main calftalk-disp">${escapeHtml((phrase.left || []).join(' '))}</span>
            <span class="calftalk-config__phrase-sub">${escapeHtml((phrase.right || []).join(' '))}</span>
          </button>`
      )
      .join('');
  }

  #renderSizes() {
    const container = this.querySelector('[data-calftalk-sizes]');
    if (!container) return;
    const reference = this.state.series.phrases.find((phrase) => phrase.variants);
    if (!reference) {
      container.innerHTML = '';
      return;
    }
    container.innerHTML = reference.variants
      .map(
        (variant) => `
          <button type="button" class="calftalk-config__opt" data-calftalk-size="${escapeHtml(variant.size)}" aria-pressed="false">
            ${escapeHtml(variant.size)}
          </button>`
      )
      .join('');
  }

  /** The variant that would be added to the cart for the current state. */
  #activeVariant() {
    const { series, selected, size, pack } = this.state;
    if (series.status !== 'live' || !selected.length) return null;

    if (pack.id === 'one') {
      const phrase = this.#lastSelected;
      if (!phrase?.variants) return null;
      return phrase.variants.find((variant) => variant.size === size) || null;
    }

    if (!pack.variants) return null;
    return pack.variants.find((variant) => variant.size === size) || pack.variants[0] || null;
  }

  #sync() {
    const { series, selected, size, pack } = this.state;
    const strings = this.data.strings;
    const isLive = series.status === 'live';
    const maxSlots = isLive ? pack.pairs : 1;

    // Pressed states.
    this.querySelectorAll('[data-calftalk-series]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.calftalkSeries === series.handle));
    });
    this.querySelectorAll('[data-calftalk-phrase]').forEach((button) => {
      const index = selected.findIndex((item) => String(item.key) === button.dataset.calftalkPhrase);
      button.setAttribute('aria-pressed', String(index >= 0));
      const badge = button.querySelector('.calftalk-config__slot-badge');
      if (maxSlots > 1 && index >= 0) {
        if (badge) badge.textContent = `${index + 1}/${maxSlots}`;
        else {
          const span = document.createElement('span');
          span.className = 'calftalk-config__slot-badge';
          span.textContent = `${index + 1}/${maxSlots}`;
          button.querySelector('.calftalk-config__phrase-sub')?.after(span);
        }
      } else if (badge) {
        badge.remove();
      }
    });
    this.querySelectorAll('[data-calftalk-size]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.calftalkSize === size));
      const phrase = pack.id === 'one' ? this.#lastSelected : null;
      const variants = pack.id === 'one' ? phrase?.variants : pack.variants;
      const variant = variants?.find((item) => item.size === button.dataset.calftalkSize);
      button.toggleAttribute('data-unavailable', Boolean(variant && !variant.available));
    });
    this.querySelectorAll('[data-calftalk-pack]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.calftalkPack === pack.id));
    });

    // Stage: real product photo when the phrase has one, SVG pair otherwise.
    const phrase = this.#lastSelected;
    if (phrase) this.#renderStage(series, phrase);
    const captionSeries = this.querySelector('[data-calftalk-caption-series]');
    if (captionSeries) captionSeries.textContent = isLive && size ? `${series.title} · ${size}` : series.title;
    const captionPhrase = this.querySelector('[data-calftalk-caption-phrase]');
    if (captionPhrase && phrase) captionPhrase.textContent = phraseLabel(phrase);

    // Series copy.
    const seriesName = this.querySelector('[data-calftalk-series-name]');
    if (seriesName) seriesName.textContent = series.title;
    const seriesDesc = this.querySelector('[data-calftalk-series-desc]');
    if (seriesDesc) seriesDesc.textContent = series.description || '';

    // Slots label.
    const slotsLabel = this.querySelector('[data-calftalk-slots-label]');
    if (slotsLabel) {
      slotsLabel.textContent =
        maxSlots > 1
          ? strings.chosenCount.replace('[X]', String(selected.length)).replace('[N]', String(maxSlots))
          : strings.leftRight;
    }

    // Live vs waitlist visibility.
    const buy = this.querySelector('[data-calftalk-buy]');
    const sizeField = this.querySelector('[data-calftalk-size-field]');
    const packField = this.querySelector('[data-calftalk-pack-field]');
    if (buy) buy.hidden = !isLive;
    if (sizeField) sizeField.hidden = !isLive;
    if (packField) packField.hidden = !isLive;
    this.querySelectorAll('[data-calftalk-waitlist]').forEach((element) => {
      element.hidden = isLive || element.dataset.calftalkWaitlist !== series.handle;
    });
    if (!isLive) return;

    // Form sync.
    const variant = this.#activeVariant();
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    const variantInput = form?.querySelector('input[name="id"]');
    if (variantInput && variant) variantInput.value = String(variant.id);

    const propertiesContainer = this.querySelector('[data-calftalk-properties]');
    if (propertiesContainer) {
      propertiesContainer.innerHTML = '';
      if (pack.id !== 'one') {
        const seriesProp = document.createElement('input');
        seriesProp.type = 'hidden';
        seriesProp.name = `properties[${strings.seriesProperty}]`;
        seriesProp.value = series.title;
        propertiesContainer.appendChild(seriesProp);
        selected.forEach((item, index) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${strings.phraseProperty} ${index + 1}]`;
          input.value = phraseLabel(item);
          propertiesContainer.appendChild(input);
        });
      }
    }

    const productForm = this.querySelector('product-form-component');
    if (productForm && phrase) {
      if (pack.id === 'one') {
        productForm.dataset.productId = String(phrase.key);
        if (phrase.url) productForm.dataset.productUrl = phrase.url;
      } else {
        productForm.dataset.productId = String(pack.productId);
      }
    }

    // Add button state and label.
    const addButton = this.querySelector('.calftalk-config__add');
    if (addButton) {
      const missing = maxSlots - selected.length;
      const available = Boolean(variant && variant.available);
      const complete = missing <= 0;
      addButton.disabled = !available || !complete;
      let label;
      if (!available) {
        label = strings.soldOut;
      } else if (!complete) {
        label = strings.chooseCount.replace('[N]', String(missing));
      } else {
        label = `${strings.addToCart} · ${variant.price}`;
      }
      // Replace only the text node so the animation markup survives.
      const textNode = Array.from(addButton.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim().length
      );
      if (textNode) textNode.textContent = label;
      else addButton.prepend(document.createTextNode(label));
    }
  }
}

if (!customElements.get('calftalk-configurator')) {
  customElements.define('calftalk-configurator', CalftalkConfigurator);
}
